import { z } from 'zod'

import {
  expenseCategorySchema,
  idSchema,
  isoDateSchema,
  isoInstantSchema,
  labelSchema,
  moneyPaisa,
  noteSchema,
  positivePaisaSchema,
  postingStatusSchema,
  recurringModeSchema,
  versionSchema,
  yearMonthSchema,
} from '../common'

/**
 * A31–A35 · Monthly costs (design screens 13 and 14, §4.14).
 *
 * The two modes are the whole design. `FIXED_AUTO` is rent: the same number
 * every month, posted by the nightly job without asking. `VARIABLE_CONFIRM` is
 * wanda: the amount changes, so the job creates a **pending** row with no
 * amount and no expense, and waits. It never invents a figure — an invented
 * wanda bill would suppress the feed estimate for a month that was never
 * actually bought (§4.3), which is worse than having no bill at all.
 */

export const recurringPostingSchema = z.object({
  id: idSchema,
  recurringExpenseId: idSchema,
  periodMonth: yearMonthSchema,
  status: postingStatusSchema,
  amountPaisa: moneyPaisa(),
  expenseId: idSchema.nullish(),
  confirmedByName: z.string().nullish(),
  confirmedAt: isoInstantSchema.nullish(),
})

export const recurringExpenseSchema = z.object({
  id: idSchema,
  category: expenseCategorySchema,
  label: labelSchema,
  amountPaisa: moneyPaisa(),
  mode: recurringModeSchema,
  dayOfMonth: z.number().int().min(1).max(28),
  startMonth: yearMonthSchema,
  endMonth: yearMonthSchema.nullish(),
  isActive: z.boolean(),
  /** From the category table: a bulk-feed template replaces the daily estimate. */
  suppressesFeedEstimate: z.boolean(),
  /**
   * This month's posting, so screen 13 can render Auto / Entered / **Confirm**
   * without a second query per row.
   */
  currentPosting: recurringPostingSchema.nullish(),
  createdAt: isoInstantSchema,
  version: versionSchema,
})

/* ── A31 · GET /recurring-expenses ────────────────────────────────────────── */

export const recurringListQuery = z.object({
  month: yearMonthSchema.optional(),
  includeInactive: z.coerce.boolean().optional(),
})

export const recurringListResponse = z.object({
  month: yearMonthSchema,
  asOf: isoDateSchema,
  totalPaisa: moneyPaisa(),
  /** Held animals this month — the divisor behind "per animal per day". */
  animals: z.number().int().nonnegative(),
  perAnimalPerDayPaisa: moneyPaisa(),
  items: z.array(recurringExpenseSchema),
})

/* ── A32 · POST · A33 · PATCH / DELETE ────────────────────────────────────── */

/**
 * `dayOfMonth` is capped at 28 so a template cannot silently skip February.
 * `amountPaisa` is required for `FIXED_AUTO` and meaningless for
 * `VARIABLE_CONFIRM`, where it is at most a hint for the confirm sheet.
 */
export const createRecurringExpenseRequest = z
  .object({
    category: expenseCategorySchema,
    label: labelSchema,
    amountPaisa: positivePaisaSchema.optional(),
    mode: recurringModeSchema,
    dayOfMonth: z.number().int().min(1).max(28).default(1),
    startMonth: yearMonthSchema,
    endMonth: yearMonthSchema.optional(),
    note: noteSchema.optional(),
  })
  .superRefine((v, ctx) => {
    if (v.mode === 'FIXED_AUTO' && v.amountPaisa === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['amountPaisa'],
        message: 'A cost that posts itself needs an amount.',
      })
    }
    if (v.endMonth && v.endMonth < v.startMonth) {
      ctx.addIssue({
        code: 'custom',
        path: ['endMonth'],
        message: 'It ends before it starts.',
      })
    }
  })

export const patchRecurringExpenseRequest = z
  .object({
    category: expenseCategorySchema.optional(),
    label: labelSchema.optional(),
    amountPaisa: positivePaisaSchema.optional(),
    mode: recurringModeSchema.optional(),
    dayOfMonth: z.number().int().min(1).max(28).optional(),
    endMonth: yearMonthSchema.nullish(),
    isActive: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'Nothing to change.')

/* ── A34 · POST /recurring-postings/:id/confirm ───────────────────────────── */

/**
 * The "Confirm" button on this month's wanda. Writes the expense spanning the
 * whole month, which then feeds §4.3 suppression if the category is bulk feed —
 * the estimate switches off for exactly the days the bill covers, and the
 * trader watches the Feed line change from estimate to actual.
 *
 * `UNIQUE (recurring_expense_id, period_month)` means a double tap, a retry or
 * a restarted job cannot post it twice.
 */
export const confirmPostingRequest = z.object({
  amountPaisa: positivePaisaSchema,
  note: noteSchema.optional(),
})

export const postingResultResponse = z.object({
  posting: recurringPostingSchema,
  expenseId: idSchema.nullish(),
  /** Days of daily-rate estimate this bill just replaced (BR §6). */
  suppressedDays: z.number().int().nonnegative().optional(),
})

/* ── A35 · POST /recurring-postings/:id/skip ──────────────────────────────── */

/** Skipping is a decision worth a reason: "no wanda bought this month". */
export const skipPostingRequest = z.object({ reason: noteSchema.optional() })

export type RecurringExpense = z.infer<typeof recurringExpenseSchema>
export type RecurringPosting = z.infer<typeof recurringPostingSchema>
export type RecurringListQuery = z.input<typeof recurringListQuery>
export type RecurringListResponse = z.infer<typeof recurringListResponse>
export type CreateRecurringExpenseRequest = z.input<typeof createRecurringExpenseRequest>
export type PatchRecurringExpenseRequest = z.input<typeof patchRecurringExpenseRequest>
export type ConfirmPostingRequest = z.input<typeof confirmPostingRequest>
export type PostingResultResponse = z.infer<typeof postingResultResponse>
